import React from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  autopilotCreditsShortfall,
  blogFetchNotices,
  selectBlogAutopilotPrice,
  selectBlogCreditsBalance,
  selectBlogNotices,
} from '@/store/slices/blog';

import type { ImportantNotice } from './useImportantNotices';

/**
 * The Autopilot Spotlight notices (`docs/auto-generation.md` in spark-blog):
 * "N new articles" while the blog has published articles of its own the owner
 * has not looked at, and "Autopilot needs credits" while a refused run's
 * notice is open. Read from `GET .../notices` for the DISPLAYED brand on
 * mount, on tab return and on brand switch, with no interval: a run happens
 * every two days, so polling would be requests for nothing.
 *
 * Brand scoping is the slice's: the request goes out under the active brand,
 * a brand switch resets the slice (`resetBlog`, so the previous brand's
 * notices never render as this one's) and an answer that lands after a switch
 * is dropped by the thunk's own stamp. What this hook adds is WHEN to ask:
 * only once the displayed brand has caught up with the active one, because
 * mid-fade the two differ and the answer would belong to the brand still
 * arriving. A 404 (no tenant, or an older service build) resolves to no
 * notices, so the cards simply do not appear.
 *
 * The credits card shows only while the owner's balance is short of what the
 * next article costs: the notice stays open on the service until a run
 * succeeds or something closes it, and the layout closes it once it sees a
 * balance that covers it (`useAutopilotCreditsSettle`), so a balance topped up
 * elsewhere must not be asked to top up again meanwhile.
 *
 * Self-contained like the other notice hooks; the callbacks that act on the
 * cards live on Home, which also re-reads after a top-up.
 */
export const useBlogNotices = (): ImportantNotice[] => {
  const dispatch = useAppDispatch();
  const brandId = useAppSelector((s) => s.casinoInstance.displayBrand?.id);
  const activeBrandId = useAppSelector((s) => s.casinoInstance.activeBrand?.id);
  const notices = useAppSelector(selectBlogNotices);
  const balance = useAppSelector(selectBlogCreditsBalance);
  const price = useAppSelector(selectBlogAutopilotPrice);

  // The displayed brand is the one the slice asks under: not mid-switch.
  const settled = brandId !== undefined && brandId === activeBrandId;

  React.useEffect(() => {
    if (!settled) return undefined;
    void dispatch(blogFetchNotices());
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') void dispatch(blogFetchNotices());
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
    // `brandId` is the brand-switch trigger: a new brand is a new read.
  }, [dispatch, settled, brandId]);

  return React.useMemo(() => {
    if (!settled || brandId === undefined || notices === null) return [];
    const list: ImportantNotice[] = [];
    // Credits first: it is the one that stops the blog, and the strip reads left to right.
    const needed = autopilotCreditsShortfall({ needed: notices.credits?.needed ?? null, balance, price });
    if (needed !== null) {
      list.push({ kind: 'blog-credits', tone: 'action', blogCredits: { brandId, needed } });
    }
    if (notices.articles.length > 0) {
      list.push({
        kind: 'blog-new-articles',
        tone: 'lumio',
        blogArticles: { brandId, ids: notices.articles.map((article) => article.id) },
      });
    }
    return list;
  }, [settled, brandId, notices, balance, price]);
};

export default useBlogNotices;
